/**
 * Mock Data Utilities
 * Provides realistic mock data for GitHub repositories and S3 buckets
 */

// Seeded random number generator for deterministic mock data
const seededRandom = (seed) => {
  let value = seed;
  return () => {
    value = (value * 9301 + 49297) % 233280;
    return value / 233280;
  };
};

// Realistic GitHub repository names (org/repo format)
export const GITHUB_REPOS = [
  { owner: 'facebook', name: 'react', fullName: 'facebook/react' },
  { owner: 'microsoft', name: 'vscode', fullName: 'microsoft/vscode' },
  { owner: 'google', name: 'material-design', fullName: 'google/material-design' },
  { owner: 'vercel', name: 'next.js', fullName: 'vercel/next.js' },
  { owner: 'kubernetes', name: 'kubernetes', fullName: 'kubernetes/kubernetes' },
  { owner: 'microsoft', name: 'TypeScript', fullName: 'microsoft/TypeScript' },
  { owner: 'facebook', name: 'react-native', fullName: 'facebook/react-native' },
  { owner: 'angular', name: 'angular', fullName: 'angular/angular' },
  { owner: 'vuejs', name: 'vue', fullName: 'vuejs/vue' },
  { owner: 'golang', name: 'go', fullName: 'golang/go' },
  { owner: 'dotnet', name: 'core', fullName: 'dotnet/core' },
  { owner: 'rails', name: 'rails', fullName: 'rails/rails' },
  { owner: 'django', name: 'django', fullName: 'django/django' },
  { owner: 'spring-projects', name: 'spring-framework', fullName: 'spring-projects/spring-framework' },
  { owner: 'nodejs', name: 'node', fullName: 'nodejs/node' },
  { owner: 'microsoft', name: 'PowerToys', fullName: 'microsoft/PowerToys' },
  { owner: 'facebook', name: 'jest', fullName: 'facebook/jest' },
  { owner: 'hashicorp', name: 'terraform', fullName: 'hashicorp/terraform' },
  { owner: 'ansible', name: 'ansible', fullName: 'ansible/ansible' },
  { owner: 'pytorch', name: 'pytorch', fullName: 'pytorch/pytorch' },
  { owner: 'tensorflow', name: 'tensorflow', fullName: 'tensorflow/tensorflow' },
  { owner: 'mozilla', name: 'firefox-browser', fullName: 'mozilla/firefox-browser' },
  { owner: 'chromium', name: 'chromium', fullName: 'chromium/chromium' },
];

// Realistic S3 bucket names (AWS-like patterns)
export const S3_BUCKETS = [
  { name: 'prod-logs-backup', region: 'us-east-1' },
  { name: 'staging-assets', region: 'us-west-2' },
  { name: 'dev-user-uploads', region: 'us-east-1' },
  { name: 'prod-database-dumps', region: 'us-east-1' },
  { name: 'analytics-warehouse', region: 'us-west-2' },
  { name: 'prod-media-storage', region: 'eu-west-1' },
  { name: 'staging-config-files', region: 'us-west-2' },
  { name: 'prod-application-logs', region: 'us-east-1' },
  { name: 'dev-test-data', region: 'us-east-1' },
  { name: 'prod-cdn-assets', region: 'ap-south-1' },
  { name: 'staging-backups', region: 'us-west-2' },
  { name: 'prod-secrets-vault', region: 'us-east-1' },
  { name: 'dev-build-artifacts', region: 'us-east-1' },
  { name: 'prod-customer-data', region: 'eu-west-1' },
  { name: 'staging-documentation', region: 'us-west-2' },
  { name: 'prod-monitoring-data', region: 'us-east-1' },
  { name: 'dev-experimental-storage', region: 'us-east-1' },
  { name: 'prod-api-cache', region: 'ap-south-1' },
  { name: 'staging-archives', region: 'us-west-2' },
  { name: 'prod-financial-records', region: 'us-east-1' },
  { name: 'dev-mock-data', region: 'us-east-1' },
  { name: 'prod-compliance-reports', region: 'eu-west-1' },
];

// Detector types for GitHub findings
const GITHUB_DETECTOR_TYPES = [
  'GitHub Token',
  'API Key',
  'AWS Access Key',
  'Private Key',
  'Password',
  'SSH Key',
  'Database Credentials',
  'Slack Token',
  'JWT Secret',
  'OAuth Token',
];

// Detector types for S3 findings
const S3_DETECTOR_TYPES = [
  'AWS Key',
  'API Key',
  'Database Credentials',
  'GitHub Token',
  'SSH Key',
  'Private Key',
  'Password',
  'Slack Token',
  'OAuth Token',
  'Encryption Key',
];

// Common file extensions for GitHub
const GITHUB_FILE_EXTENSIONS = ['.js', '.ts', '.json', '.env', '.yaml', '.yml', '.config', '.properties', '.py', '.java', '.go', '.rb', '.php'];

// Common file extensions for S3
const S3_FILE_EXTENSIONS = ['.json', '.csv', '.log', '.txt', '.config', '.sql', '.dump', '.backup', '.env', '.properties'];

// Common email domains
const EMAIL_DOMAINS = ['example.com', 'company.com', 'corp.io', 'dev.org', 'test.net', 'prod.co', 'staging.io'];

/**
 * Generate realistic GitHub findings
 * @param {string} repoId - Repository ID
 * @param {string} owner - Repository owner
 * @param {string} repoName - Repository name
 * @param {number} count - Number of findings to generate
 * @returns {Array} Array of mock findings
 */
export const generateGitHubFindings = (repoId, owner, repoName, count = 50) => {
  const findings = [];
  
  // Create a seeded RNG based on repoId to ensure deterministic data
  // Use repoId as primary component to ensure uniqueness per integration
  const seed = hashStringToInt(`github-${repoId}-${owner || 'unknown'}-${repoName || repoId}`);
  const random = seededRandom(seed);
  const baseTimestamp = new Date('2024-01-01').getTime();
  
  for (let i = 0; i < count; i++) {
    const detectorType = GITHUB_DETECTOR_TYPES[Math.floor(random() * GITHUB_DETECTOR_TYPES.length)];
    const fileExt = GITHUB_FILE_EXTENSIONS[Math.floor(random() * GITHUB_FILE_EXTENSIONS.length)];
    const fileName = `config${Math.floor(random() * 100)}${fileExt}`;
    const filePathRandom = random();
    const filePath = filePathRandom > 0.5 
      ? `src/${fileName}` 
      : filePathRandom > 0.25
        ? `config/${fileName}`
        : `.env${Math.floor(random() * 3) === 0 ? '.local' : ''}`;
    
    const emailDomain = EMAIL_DOMAINS[Math.floor(random() * EMAIL_DOMAINS.length)];
    const firstNames = ['john', 'jane', 'alex', 'sarah', 'mike', 'emily', 'david', 'lisa'];
    const lastNames = ['doe', 'smith', 'johnson', 'williams'];
    const author = `${firstNames[Math.floor(random() * firstNames.length)]}.${lastNames[Math.floor(random() * lastNames.length)]}@${emailDomain}`;
    
    const verified = random() > 0.6; // ~40% verified
    const isIgnored = !verified && random() > 0.8; // ~20% of unverified are ignored
    
    // Use deterministic dates based on seed
    const daysAgo = Math.floor(random() * 90);
    const daysAgoUpdated = Math.floor(random() * 30);
    const created_at = new Date(baseTimestamp + daysAgo * 24 * 60 * 60 * 1000 + i * 1000).toISOString();
    const updated_at = new Date(baseTimestamp + daysAgoUpdated * 24 * 60 * 60 * 1000 + i * 1000).toISOString();
    
    findings.push({
      id: `finding-github-${repoId}-${i + 1}`,
      scan_id: `scan-${repoId}-${i + 1}`,
      repository_id: repoId,
      detector_type: detectorType,
      verified: verified,
      raw_result: `mock-secret-${detectorType.toLowerCase().replace(/\s+/g, '-')}-${i + 1}`,
      file_path: filePath,
      file_name: fileName,
      email: author,
      author: author,
      status: isIgnored ? 'ignore' : (verified ? 'active' : 'unvalidated'),
      created_at: created_at,
      updated_at: updated_at,
      link: `https://github.com/${owner}/${repoName}/blob/main/${filePath}`,
      has_insights: verified && random() > 0.7, // ~30% of verified have insights
    });
  }
  
  return findings;
};

// Helper function to convert string to integer seed
function hashStringToInt(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Generate realistic S3 findings
 * @param {string} bucketId - Bucket ID
 * @param {string} bucketName - Bucket name
 * @param {number} count - Number of findings to generate
 * @returns {Array} Array of mock findings
 */
export const generateS3Findings = (bucketId, bucketName, count = 50) => {
  const findings = [];
  
  // Create a seeded RNG based on bucketId to ensure deterministic data
  // Use bucketId as primary component to ensure uniqueness per integration
  const seed = hashStringToInt(`s3-${bucketId}-${bucketName || bucketId}`);
  const random = seededRandom(seed);
  const baseTimestamp = new Date('2024-01-01').getTime();
  const prefixes = ['config', 'backup', 'dump', 'log', 'data', 'export'];
  const emailPrefixes = ['admin', 'system', 'backup', 'automation'];
  
  for (let i = 0; i < count; i++) {
    const detectorType = S3_DETECTOR_TYPES[Math.floor(random() * S3_DETECTOR_TYPES.length)];
    const fileExt = S3_FILE_EXTENSIONS[Math.floor(random() * S3_FILE_EXTENSIONS.length)];
    const fileName = `${prefixes[Math.floor(random() * prefixes.length)]}${Math.floor(random() * 1000)}${fileExt}`;
    const filePathRandom = random();
    const filePath = filePathRandom > 0.5 
      ? `config/${fileName}` 
      : filePathRandom > 0.25
        ? `backups/${fileName}`
        : fileName;
    
    const emailDomain = EMAIL_DOMAINS[Math.floor(random() * EMAIL_DOMAINS.length)];
    const email = `${emailPrefixes[Math.floor(random() * emailPrefixes.length)]}@${emailDomain}`;
    
    const verified = random() > 0.6; // ~40% verified
    const isIgnored = !verified && random() > 0.8; // ~20% of unverified are ignored
    
    // Use deterministic dates based on seed
    const daysAgo = Math.floor(random() * 90);
    const daysAgoUpdated = Math.floor(random() * 30);
    const created_at = new Date(baseTimestamp + daysAgo * 24 * 60 * 60 * 1000 + i * 1000).toISOString();
    const updated_at = new Date(baseTimestamp + daysAgoUpdated * 24 * 60 * 60 * 1000 + i * 1000).toISOString();
    
    findings.push({
      id: `finding-s3-${bucketId}-${i + 1}`,
      scan_id: `scan-${bucketId}-${i + 1}`,
      bucket_id: bucketId,
      detector_type: detectorType,
      verified: verified,
      raw_result: `mock-secret-${detectorType.toLowerCase().replace(/\s+/g, '-')}-${i + 1}`,
      file_path: filePath,
      file_name: fileName,
      email: email,
      status: isIgnored ? 'ignore' : (verified ? 'active' : 'unvalidated'),
      created_at: created_at,
      updated_at: updated_at,
      has_insights: verified && random() > 0.7, // ~30% of verified have insights
    });
  }
  
  return findings;
};

/**
 * Get a GitHub repository by index
 * @param {number} index - Index of the repository
 * @returns {Object} Repository object
 */
export const getGitHubRepo = (index) => {
  return GITHUB_REPOS[index % GITHUB_REPOS.length];
};

/**
 * Get an S3 bucket by index
 * @param {number} index - Index of the bucket
 * @returns {Object} Bucket object
 */
export const getS3Bucket = (index) => {
  return S3_BUCKETS[index % S3_BUCKETS.length];
};

/**
 * Generate realistic Active Directory findings
 * @param {string} domainId - Domain ID
 * @param {string} domainName - Domain name
 * @param {number} count - Number of findings to generate
 * @returns {Array} Array of mock findings
 */
export const generateActiveDirectoryFindings = (domainId, domainName, count = 50) => {
  const findings = [];
  
  // Create a seeded RNG based on domainId to ensure deterministic data
  // Use domainId as primary component to ensure uniqueness per integration
  const seed = hashStringToInt(`active-directory-${domainId}-${domainName || domainId}`);
  const random = seededRandom(seed);
  
  // Service account prefixes
  const servicePrefixes = ['svc', 'bind', 'intranet', 'app', 'backup', 'dr', 'admin', 'system', 'automation', 'msol'];
  const serviceSuffixes = ['01', '02', 'backup', 'manager', 'service', 'api', 'worker', 'scheduler'];
  const riskLevels = ['Low', 'Medium', 'High'];
  const predictabilityLevels = ['Low', 'Medium', 'High'];
  
  // Extract domain from domainName (e.g., "ad.acaws.silverfort.io" -> "ad.acaws.silverfort.io")
  const domain = domainName || 'example.local';
  
  for (let i = 0; i < count; i++) {
    const prefix = servicePrefixes[Math.floor(random() * servicePrefixes.length)];
    const suffix = serviceSuffixes[Math.floor(random() * serviceSuffixes.length)];
    const accountEmail = `${prefix}_${suffix}@${domain}`;
    
    const sourceCount = Math.floor(random() * 10) + 1;
    const destinationCount = Math.floor(random() * 8) + 1;
    const authCount = Math.floor(random() * 100000) + 100;
    const riskLevel = riskLevels[Math.floor(random() * riskLevels.length)];
    const predictability = predictabilityLevels[Math.floor(random() * predictabilityLevels.length)];
    const isProtected = random() > 0.4; // ~60% protected
    const verified = random() > 0.3; // ~70% verified
    
    findings.push({
      id: `finding-ad-${domainId}-${i + 1}`,
      account_email: accountEmail,
      source_count: sourceCount,
      destination_count: destinationCount,
      auth_count: authCount,
      risk_level: riskLevel,
      predictability: predictability,
      is_protected: isProtected,
      verified: verified,
      status: verified ? 'active' : 'unvalidated',
      created_at: new Date('2024-01-01').toISOString(),
      updated_at: new Date().toISOString(),
    });
  }
  
  return findings;
};
